import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  MdAdd, MdSearch, MdInventory2, MdDesignServices,
  MdEdit, MdDelete, MdVisibility, MdWarning
} from 'react-icons/md';
import { inventarioAPI } from '../services/api';
import { useModal } from '../context/ModalContext';
import { useConfig } from '../context/ConfigContext';
import { SkeletonTableRows, EmptyState } from '../components/SkeletonLoader';

/* ─── Styled ─── */
const Container = styled.div`max-width: 1400px;`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.xl};
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const TitleGroup = styled.div``;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  svg { color: ${p => p.theme.colors.primary}; }
`;

const Subtitle = styled.p`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  align-items: center;
  flex-wrap: wrap;
`;

const AddBtn = styled.button`
  padding: 10px ${p => p.theme.spacing.lg};
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  box-shadow: 0 4px 14px ${p => p.theme.colors.primary}40;

  &:hover { transform: translateY(-1px); box-shadow: 0 6px 20px ${p => p.theme.colors.primary}50; }
`;

const FilterSection = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  display: flex;
  gap: ${p => p.theme.spacing.md};
  align-items: center;
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`position: relative; flex: 1; min-width: 200px;`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${p => p.theme.colors.textLight};
  font-size: 1.2rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.md} ${p => p.theme.spacing.md} 2.5rem;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const FilterToggle = styled.div`
  display: flex;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  overflow: hidden;
`;

const ToggleBtn = styled.button`
  padding: 8px 16px;
  border: none;
  background: ${p => p.active ? p.theme.colors.primary : p.theme.colors.background};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.active ? 600 : 400};
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: ${p => p.theme.spacing.lg};
`;

const Card = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  box-shadow: ${p => p.theme.shadows.card};
  overflow: hidden;
  transition: ${p => p.theme.transitions.fast};
  border: 1px solid ${p => p.theme.colors.borderLight};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${p => p.theme.shadows.lg};
    border-color: ${p => p.theme.colors.primary}30;
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 160px;
  background: ${p => p.theme.colors.backgroundDark};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    font-size: 3.5rem;
    color: ${p => p.theme.colors.textLight};
    opacity: 0.4;
  }
`;

const StockBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 3px 10px;
  border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  background: ${p => p.low
    ? p.theme.colors.danger + 'ee'
    : p.zero
    ? p.theme.colors.textSecondary + 'ee'
    : p.theme.colors.success + 'ee'};
  color: white;
  display: flex;
  align-items: center;
  gap: 4px;
  svg { font-size: 0.9rem; }
`;

const CardBody = styled.div`
  padding: ${p => p.theme.spacing.lg};
`;

const CardName = styled.h3`
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 6px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: ${p => p.theme.spacing.md};
`;

const MetaRow = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
`;

const CardDesc = styled.p`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  margin: 0 0 ${p => p.theme.spacing.md} 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Price = styled.div`
  font-size: ${p => p.theme.fontSizes.xl};
  font-weight: 800;
  color: ${p => p.theme.colors.primary};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const CardActions = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
`;

const ActionBtn = styled.button`
  flex: 1;
  padding: 7px;
  border: 1px solid ${p => p.danger ? p.theme.colors.danger + '40' : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.danger ? p.theme.colors.danger + '10' : p.theme.colors.background};
  color: ${p => p.danger ? p.theme.colors.danger : p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  svg { font-size: 1rem; }

  &:hover {
    background: ${p => p.danger ? p.theme.colors.danger + '20' : p.theme.colors.backgroundDark};
    border-color: ${p => p.danger ? p.theme.colors.danger : p.theme.colors.primary};
    color: ${p => p.danger ? p.theme.colors.danger : p.theme.colors.primary};
  }
`;

const StockControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const StockLabel = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  flex: 1;
`;

const StockInput = styled.input`
  width: 64px;
  padding: 4px 8px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 6px;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 700;
  text-align: center;
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const StockSaveBtn = styled.button`
  padding: 4px 10px;
  background: ${p => p.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
`;

const EmptyWrap = styled.div`
  grid-column: 1 / -1;
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  box-shadow: ${p => p.theme.shadows.card};
  padding: ${p => p.theme.spacing.xl};
`;

const formatPrice = (p) =>
  Number(p || 0).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

const Inventario = () => {
  const navigate = useNavigate();
  const { confirm, toast } = useModal();
  const { config } = useConfig();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [stockEdits, setStockEdits] = useState({});

  useEffect(() => {
    loadAll();
  }, []); // eslint-disable-line

  const loadAll = async () => {
    try {
      setLoading(true);
      const itemsRes = await inventarioAPI.getAll();
      setItems(itemsRes.data.data || []);
    } catch (err) {
      toast(err.message || 'Error al cargar', 'error');
    } finally {
      setLoading(false);
    }
  };

  const tipoActual = config?.tipo_negocio === 'servicios' ? 'servicio' : 'producto';

  const filtered = items.filter(i => {
    if (i.tipo !== tipoActual) return false;
    if (!showInactive && !i.activo) return false;
    const q = searchTerm.toLowerCase();
    return !q || i.nombre?.toLowerCase().includes(q) || i.descripcion?.toLowerCase().includes(q) || i.sku?.toLowerCase().includes(q);
  });

  const handleDelete = async (item) => {
    const ok = await confirm(`¿Eliminar "${item.nombre}"?`, {
      title: 'Eliminar ' + (tipoActual === 'producto' ? 'producto' : 'servicio'),
      confirmText: 'Eliminar',
      danger: true,
    });
    if (!ok) return;
    try {
      await inventarioAPI.delete(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast('Eliminado correctamente', 'success');
    } catch (err) {
      toast(err.message || 'Error al eliminar', 'error');
    }
  };

  const handleStockSave = async (item) => {
    const newStock = stockEdits[item.id];
    if (newStock === undefined || newStock === String(item.stock)) return;
    try {
      const res = await inventarioAPI.updateStock(item.id, Number(newStock));
      setItems(prev => prev.map(i => i.id === item.id ? res.data.data : i));
      setStockEdits(prev => { const n = { ...prev }; delete n[item.id]; return n; });
      toast('Stock actualizado', 'success');
    } catch (err) {
      toast(err.message || 'Error al actualizar stock', 'error');
    }
  };

  const isProducto = tipoActual === 'producto';

  return (
    <Container>
      <PageHeader>
        <TitleGroup>
          <Title>
            {isProducto ? <MdInventory2 /> : <MdDesignServices />}
            {isProducto ? 'Inventario' : 'Servicios'}
          </Title>
          <Subtitle>
            {isProducto
              ? `${filtered.length} producto${filtered.length !== 1 ? 's' : ''} en inventario`
              : `${filtered.length} servicio${filtered.length !== 1 ? 's' : ''} registrados`}
          </Subtitle>
        </TitleGroup>
        <HeaderActions>
          <AddBtn onClick={() => navigate('/inventario/nuevo')}>
            <MdAdd />
            {isProducto ? 'Nuevo Producto' : 'Nuevo Servicio'}
          </AddBtn>
        </HeaderActions>
      </PageHeader>

      <FilterSection>
        <SearchWrap>
          <SearchIcon />
          <SearchInput
            placeholder={isProducto ? 'Buscar por nombre, SKU...' : 'Buscar por nombre...'}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </SearchWrap>
        <FilterToggle>
          <ToggleBtn active={!showInactive} onClick={() => setShowInactive(false)}>Activos</ToggleBtn>
          <ToggleBtn active={showInactive} onClick={() => setShowInactive(true)}>Todos</ToggleBtn>
        </FilterToggle>
      </FilterSection>

      {loading ? (
        <Grid>
          {[...Array(6)].map((_, i) => (
            <Card key={i} style={{ height: 320, background: 'var(--bg-light)' }} />
          ))}
        </Grid>
      ) : (
        <Grid>
          {filtered.length === 0 ? (
            <EmptyWrap>
              <EmptyState
                type="clients"
                title={searchTerm ? 'Sin resultados' : `Sin ${isProducto ? 'productos' : 'servicios'} aun`}
                subtitle={searchTerm
                  ? 'Intenta con otro término.'
                  : `Agrega tu primer ${isProducto ? 'producto' : 'servicio'}.`}
                actionLabel={!searchTerm ? `Agregar ${isProducto ? 'Producto' : 'Servicio'}` : undefined}
                onAction={!searchTerm ? () => navigate('/inventario/nuevo') : undefined}
              />
            </EmptyWrap>
          ) : (
            filtered.map(item => {
              const imgUrl = inventarioAPI.getImageUrl(item.imagen_archivo);
              const isLowStock = isProducto && item.stock <= item.stock_minimo && item.stock > 0;
              const isZeroStock = isProducto && item.stock === 0;
              const stockVal = stockEdits[item.id] !== undefined ? stockEdits[item.id] : String(item.stock ?? 0);

              return (
                <Card key={item.id}>
                  <CardImage>
                    {imgUrl
                      ? <img src={imgUrl} alt={item.nombre} />
                      : (isProducto ? <MdInventory2 /> : <MdDesignServices />)
                    }
                    {isProducto && (
                      <StockBadge low={isLowStock} zero={isZeroStock}>
                        {isZeroStock && <MdWarning />}
                        {isZeroStock ? 'Sin stock' : isLowStock ? `Stock bajo: ${item.stock}` : `Stock: ${item.stock}`}
                      </StockBadge>
                    )}
                  </CardImage>

                  <CardBody>
                    <CardName>{item.nombre}</CardName>
                    <CardMeta>
                      {item.sku && <MetaRow>SKU: {item.sku}</MetaRow>}
                      {item.categoria && <MetaRow>Categoria: {item.categoria}</MetaRow>}
                      {!isProducto && item.duracion_minutos && (
                        <MetaRow>
                          Duracion: {item.duracion_minutos} {item.duracion_unidad || 'minutos'}
                        </MetaRow>
                      )}
                    </CardMeta>

                    {item.descripcion && <CardDesc>{item.descripcion}</CardDesc>}
                    <Price>{formatPrice(item.precio)}</Price>

                    {isProducto && (
                      <StockControl>
                        <StockLabel>Stock:</StockLabel>
                        <StockInput
                          type="number"
                          min="0"
                          value={stockVal}
                          onChange={e => setStockEdits(prev => ({ ...prev, [item.id]: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && handleStockSave(item)}
                        />
                        {stockEdits[item.id] !== undefined && stockEdits[item.id] !== String(item.stock) && (
                          <StockSaveBtn onClick={() => handleStockSave(item)}>OK</StockSaveBtn>
                        )}
                      </StockControl>
                    )}

                    <CardActions>
                      <ActionBtn onClick={() => navigate(`/inventario/${item.id}`)}>
                        <MdEdit /> Editar
                      </ActionBtn>
                      <ActionBtn danger onClick={() => handleDelete(item)}>
                        <MdDelete /> Eliminar
                      </ActionBtn>
                    </CardActions>
                  </CardBody>
                </Card>
              );
            })
          )}
        </Grid>
      )}
    </Container>
  );
};

export default Inventario;
